import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import { CustomLoading } from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPropertyDetail,
  updateProperty,
} from "../../../store/propertySlice";
import type { AppDispatch } from "../../../store";
import PropertyOverview from "../../../components/PropertyDetail/PropertyOverview";
import RentRoll from "../../../components/PropertyDetail/RentRoll";
import Budget from "../../../components/PropertyDetail/Budget";
import RepairSubTab from "../../../components/PropertyDetail/ExpenseManagement/RepairSubTab";
import InspectionCleaning from "../../../components/PropertyDetail/InspectionCleaning";
import ExpenseManagement from "../../../components/PropertyDetail/ExpenseManagement";
import PropertyDetailHeader from "../../../components/PropertyDetail/PropertyDetailHeader";
import {
  TAB_CONTENT_PADDING_TOP_DESKTOP,
  TAB_CONTENT_PADDING_TOP_MOBILE,
} from "../../../constants/layout";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { useRefetchOn } from "@/hooks/useRefetchOn";
import { useExpenseBudgetCategories } from "@/hooks/useExpenseBudgetCategories";
import { processFileArray } from "../../../utils/fileUpload";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import type { ApiErrorPayload, ApiErrorType } from "@/utils/apiErrorPayload";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";
import {
  DEFAULT_PROPERTY_DETAIL_TAB_ID,
  PROPERTY_DETAIL_TABS,
  isValidPropertyDetailTabQuery,
  parseTabId,
  PROPERTY_DETAIL_TAB_IDS,
  syncPropertyDetailTopTabSearchParams,
} from "./tabConfig";

// Types for property data
// Keep this local to typing only where needed; avoid conflicting with child props

type LocalUpdateValues = Record<string, any>;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  // 概要タブ (index=0) と 収支タブ (index=5) は
  // PropertyRegistrationTabs のカード風タブを使うためグレー背景で包む。
  const isCardLayoutTab = index === 0 || index === 5;
  // 概要タブ (index=0) と収支タブ (index=5) は内部にサブタブ
  //   (PropertyRegistrationTabs) を持ち、そのサブタブを固定したまま中身を
  //   スクロールさせる必要があるため、TabPanel は flex コンテナ + overflow:hidden
  //   とし、スクロール権は子に委譲する。
  //   それ以外のタブでは TabPanel 自身を overflowY: auto にして内部スクロールさせる。
  const delegateScrollToChild = index === 0 || index === 5;

  // タブ枠（ヘッダ/上部タブ）を固定したまま、中身だけスクロールさせるため
  //   ビューポート高さから上部固定領域を引いた値を高さとして与える。
  //   600px 以上 (デスクトップヘッダー表示): DashboardLayout pt(50) + タブコンテンツ pt(100) + バッファ6 = 156
  //   600px 未満 (スマホ・ハンバーガーヘッダー):  DashboardLayout pt(67) + タブコンテンツ pt(68)  + バッファ5 = 140
  //   ※ 600〜1199 は縮小レール＋デスクトップヘッダーのため、デスクトップ相当 (sm 境界) で切替。
  const fixedHeight = {
    sm: "calc(100vh - 156px)",
    xs: "calc(100vh - 140px)",
  } as const;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`property-tabpanel-${index}`}
      aria-labelledby={`property-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            height: fixedHeight,
            minHeight: 0,
            minWidth: 0,
            ...(delegateScrollToChild
              ? {
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }
              : {
                  overflowX: "hidden",
                  overflowY: "auto",
                }),
            ...(isCardLayoutTab
              ? {
                  backgroundColor: "#f5f5f5",
                  // グレー背景は全幅に出す (Layout の pl=24/32 を ml で打ち消し)
                  ml: { xs: -3, lg: -4 },
                  mr: -3,
                  pt: 1.7,
                  // 内側 padding 16px で中身を 16px から開始
                  px: 2,
                  // 概要/収支は子 (PropertyOverview / ExpenseManagement) が
                  // 自前で pb を管理する
                  pb: delegateScrollToChild ? 0 : 9,
                }
              : {
                  pt: 2,
                  // lg未満: Layout の pl=24 を 8 食い込ませて実効 16px
                  // lg以上: Layout の pl=32 を 16 食い込ませて実効 16px
                  ml: { xs: -1, lg: -2 },
                  pb: 9,
                }),
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

const PropertyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { items: budgetCategories, loading: categoriesLoading } =
    useExpenseBudgetCategories();
  // CardTab(?category=)は expense_group(カテゴリ名)単位になったため、URL検証も
  // category_id ではなくユーザー管理の expense_group の有効値で行う。
  const expenseCategoryIds = useMemo(() => {
    const set = new Set<string>();
    for (const c of budgetCategories) {
      if (c.expense_group) set.add(c.expense_group);
    }
    return Array.from(set);
  }, [budgetCategories]);
  const tabValue = useMemo(
    () =>
      parseTabId(
        searchParams.get("tab"),
        PROPERTY_DETAIL_TABS,
        DEFAULT_PROPERTY_DETAIL_TAB_ID,
      ),
    [searchParams],
  );
  const isCategoryValidationPending =
    tabValue === PROPERTY_DETAIL_TAB_IDS.MANAGEMENT &&
    searchParams.get("category") != null &&
    categoriesLoading;
  const hasValidTabQuery = useMemo(
    () =>
      isValidPropertyDetailTabQuery(
        searchParams,
        categoriesLoading ? undefined : expenseCategoryIds,
      ),
    [searchParams, categoriesLoading, expenseCategoryIds],
  );
  const { showToast } = useGlobalToast();
  const [_updatingFlag, setUpdatingFlag] = useState(false);
  const [initialLoadError, setInitialLoadError] = useState<ApiErrorType | null>(
    null,
  );

  // Redux state
  const { detailed } = useSelector((state: any) => state.property);

  // ナビゲーション state から物件名を取得（APIロード完了前のフォールバック用）
  const statePropertyName = (location.state as any)?.propName || "";

  const fetchDetail = React.useCallback(async () => {
    if (!hasValidTabQuery) return;
    setInitialLoadError(null);
    if (!id || id === "undefined") {
      setInitialLoadError("notFound");
      return;
    }
    try {
      const result = await dispatch(fetchPropertyDetail(id));
      if (!fetchPropertyDetail.fulfilled.match(result)) {
        const payload = result.payload as ApiErrorPayload | undefined;
        setInitialLoadError(payload?.errorType ?? "unknown");
        return;
      }
      if (!(result.payload as { id?: unknown }).id) {
        setInitialLoadError("notFound");
      }
    } catch (err) {
      console.error("Error fetching property detail:", err);
      setInitialLoadError("unknown");
    }
  }, [id, dispatch, hasValidTabQuery]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // AI チャットアクション完了時 (refetchBus.emit('property:<id>')) に再フェッチ
  useRefetchOn("property", id, fetchDetail);

  // ページ読み込み時に一番上にスクロール
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  // 物件詳細ページの全タブで body / html のスクロールを封じ、
  //「タブ枠は固定・中身だけ内部スクロール」を確実に成立させる。
  //  離脱時に元の overflow を復元する。
  useEffect(() => {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, []);

  const handleTabChange = (_e: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0) {
      void fetchDetail();
    }

    const cleanState = location.state ? { ...location.state } : {};
    if ("showToast" in cleanState) {
      delete (cleanState as any).showToast;
    }

    // 関数型更新で ExpenseListTab 側の setSearchParams と競合しないようにする
    setSearchParams(
      (prev) => syncPropertyDetailTopTabSearchParams(prev, newValue),
      { replace: true, state: cleanState },
    );
  };

  // 収支以外のタブで category が残った場合のフェイルセーフ（競合・直リンク対策）
  useEffect(() => {
    const tab = parseTabId(
      searchParams.get("tab"),
      PROPERTY_DETAIL_TABS,
      DEFAULT_PROPERTY_DETAIL_TAB_ID,
    );
    if (
      tab === PROPERTY_DETAIL_TAB_IDS.MANAGEMENT ||
      !searchParams.has("category")
    ) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("category");
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  const onUpdateProperty = async (data: LocalUpdateValues) => {
    if (!id) return;

    const formData = new FormData();
    const appendIfNotEmpty = (key: string, value: unknown) => {
      if (value === undefined || value === null) return;
      if (typeof value === "string" && value.trim() === "") return;
      formData.append(key, String(value));
    };
    const appendAllowEmpty = (key: string, value: unknown) => {
      if (value === undefined || value === null) return;
      formData.append(key, String(value));
    };

    // 基本情報
    appendIfNotEmpty("name", data.name);
    appendIfNotEmpty("property_category", data.property_category);
    appendIfNotEmpty("information_source", data.information_source);
    if (data.postal_code !== undefined && data.postal_code !== null) {
      const cleanedPostalCode = String(data.postal_code).replace(/-/g, "");
      if (cleanedPostalCode.trim() !== "") {
        formData.append("postal_code", cleanedPostalCode);
      }
    }
    appendIfNotEmpty("prefecture", data.prefecture);
    appendIfNotEmpty("city", data.city);
    appendIfNotEmpty("block_number", data.block_number);
    appendIfNotEmpty("price", data.price);
    appendIfNotEmpty("remarks", data.remarks);
    // 予実分析用グループ (空文字でも送信して clear できるようにする)
    appendAllowEmpty("group_name", (data as any).group_name ?? "");

    // 土地情報
    appendIfNotEmpty("lot_number", data.lot_number);
    appendAllowEmpty("land_category", data.land_category);
    appendAllowEmpty("current_condition", data.current_condition);
    appendAllowEmpty("land_rights", data.land_rights);
    appendIfNotEmpty("boundary_confirmed", data.boundary_confirmed);
    appendIfNotEmpty("land_area_registered", data.land_area_registered);
    appendIfNotEmpty("land_area_measured", data.land_area_measured);

    // 地番と境界情報（Presigned URL方式でアップロード）
    if (data.lots && Array.isArray(data.lots)) {
      // 空配列の場合は明示的に送信
      if (data.lots.length === 0) {
        formData.append("lots", JSON.stringify([]));
      }

      for (const [lotIndex, lot] of data.lots.entries()) {
        // 地番（空でも送信してlotの存在をバックエンドに伝える）
        formData.append(`lots[${lotIndex}][lot_number]`, lot.lotNumber || "");

        // 境界情報
        if (lot.boundaries && Array.isArray(lot.boundaries)) {
          for (const [boundaryIndex, boundary] of lot.boundaries.entries()) {
            // 隣地（空でも送信してboundaryの存在をバックエンドに伝える）
            formData.append(
              `lots[${lotIndex}][boundaries][${boundaryIndex}][adjacent_land]`,
              boundary.adjacentLand || "",
            );
            if (boundary.encroachment) {
              formData.append(
                `lots[${lotIndex}][boundaries][${boundaryIndex}][encroachment]`,
                boundary.encroachment,
              );
            }
            // 確定書類のアップロード（Presigned URL方式）
            if (boundary.confirmation && boundary.confirmation.length > 0) {
              const s3Keys = await processFileArray(
                boundary.confirmation,
                id!,
                false,
              );
              if (s3Keys.length > 0) {
                formData.append(
                  `lots[${lotIndex}][boundaries][${boundaryIndex}][confirmation]`,
                  JSON.stringify(s3Keys.map((key) => ({ data: key }))),
                );
              }
            }
            // 越境覚書のアップロード（Presigned URL方式）
            if (
              boundary.encroachmentMemo &&
              boundary.encroachmentMemo.length > 0
            ) {
              const s3Keys = await processFileArray(
                boundary.encroachmentMemo,
                id!,
                false,
              );
              if (s3Keys.length > 0) {
                formData.append(
                  `lots[${lotIndex}][boundaries][${boundaryIndex}][encroachment_memo]`,
                  JSON.stringify(s3Keys.map((key) => ({ data: key }))),
                );
              }
            }
          }
        }
      }
    }

    // 接道情報（配列をJSONで送信）
    if (Array.isArray(data.roads)) {
      const roadsToSend = data.roads.map((r: any) => ({
        direction: r.direction || "",
        width: r.width ? Number(r.width) : undefined,
        boundary_confirmed:
          r.boundary_confirmed != null ? r.boundary_confirmed : undefined,
      }));
      // 空の接道情報も含めて送信
      formData.append("roads", JSON.stringify(roadsToSend));
    }

    // 建物情報
    appendIfNotEmpty(
      "house_registration_number",
      data.house_registration_number,
    );
    appendIfNotEmpty("building_type", data.building_type);
    appendIfNotEmpty("structure", data.structure);
    appendIfNotEmpty("basement_floors", data.basement_floors);
    appendIfNotEmpty("ground_floors", data.ground_floors);
    appendIfNotEmpty("building_area", data.building_area);
    appendIfNotEmpty("private_area", data.private_area);
    appendIfNotEmpty("total_floor_area", data.total_floor_area);
    appendIfNotEmpty(
      "total_floor_area_measured",
      data.total_floor_area_measured,
    );
    appendIfNotEmpty("built_date", data.built_date);
    appendIfNotEmpty("inspection_certificate", data.inspection_certificate);
    appendIfNotEmpty(
      "environmental_certification",
      data.environmental_certification,
    );

    // 設備情報
    appendIfNotEmpty("equipment_electricity", data.equipment_electricity);
    appendIfNotEmpty("equipment_water_supply", data.equipment_water_supply);
    appendIfNotEmpty("equipment_sewerage", data.equipment_sewerage);
    appendIfNotEmpty("equipment_city_gas", data.equipment_city_gas);

    // 都市計画情報
    appendIfNotEmpty("usage_zone", data.usage_zone);
    appendIfNotEmpty("fire_prevention", data.fire_prevention);
    appendIfNotEmpty("height_district", data.height_district);
    appendIfNotEmpty("building_coverage_ratio", data.building_coverage_ratio);
    appendIfNotEmpty("floor_area_ratio", data.floor_area_ratio);

    // 物件画像のアップロード（Presigned URL方式）
    if (Array.isArray(data.image_urls)) {
      if (data.image_urls.length > 0) {
        const s3Keys = await processFileArray(data.image_urls, id!, false);
        if (s3Keys.length > 0) {
          formData.append(
            "image_urls",
            JSON.stringify(s3Keys.map((key) => ({ data: key }))),
          );
        }
      } else {
        // 空配列の場合は明示的に空配列を送信（削除を意味する）
        formData.append("image_urls", JSON.stringify([]));
      }
    }

    // 書類ファイルを追加（Presigned URL方式）
    const documentKeys = [
      // 登記、測量関連
      "kozu",
      "land_registry",
      "building_registry",
      "survey_map",
      "boundary_confirmation_docs",
      "encroachment_memo_docs",
      // 建築関連
      "construction_notice",
      "inspection_certificate_docs",
      "completion_drawing",
      "structural_calculation",
      // 売買、契約関連
      "sales_contract",
      "important_matters",
      "owner_change_notice",
      "insurance_certificate_docs",
      // デューデリジェンス関連
      "appraisal_report",
      "engineering_report",
      // 管理契約関連
      "property_management_contract",
      "building_management_contract",
      "electricity_contract",
      "industrial_waste_contract",
      "general_waste_contract",
      "electrical_equipment_contract",
      // 消防、防災関連
      "fire_prevention_plan",
      "overall_fire_prevention_plan",
      "chief_fire_manager_appointment",
      "fire_manager_appointment",
      "specific_building_change",
      "fire_manager_delegation",
      // 信託関連
      "trust_contract",
      "trust_certificate",
      "trust_transfer_consent",
    ];

    for (const key of documentKeys) {
      const files = data[key];
      if (Array.isArray(files)) {
        if (files.length > 0) {
          const s3Keys = await processFileArray(files, id!, false);
          if (s3Keys.length > 0) {
            formData.append(
              key,
              JSON.stringify(s3Keys.map((key) => ({ data: key }))),
            );
          }
        } else {
          // 空配列の場合は明示的に空配列を送信（削除を意味する）
          formData.append(key, JSON.stringify([]));
        }
      }
    }

    // その他の書類のアップロード（Presigned URL方式）
    const otherDocuments = (data as any)._otherDocuments;
    const currentOtherDocumentKeys = new Set(
      otherDocuments?.map((doc: any) => doc.key) || [],
    );

    // 既存の other_documents のキーを取得（削除された項目を検出するため）
    const existingOtherDocumentKeys = detailed?.data?.other_documents
      ? Object.keys(detailed.data.other_documents)
      : [];

    if (
      otherDocuments &&
      Array.isArray(otherDocuments) &&
      otherDocuments.length > 0
    ) {
      // ラベル名のマップを送信
      const otherDocumentsMap: Record<string, string> = {};
      otherDocuments.forEach((doc: any) => {
        otherDocumentsMap[doc.key] = doc.label;
      });
      formData.append(
        "other_documents_labels",
        JSON.stringify(otherDocumentsMap),
      );

      // 各その他書類のファイルを送信
      for (const doc of otherDocuments) {
        const files = data[doc.key];
        if (Array.isArray(files)) {
          if (files.length > 0) {
            const s3Keys = await processFileArray(files, id!, false);
            if (s3Keys.length > 0) {
              formData.append(
                doc.key,
                JSON.stringify(s3Keys.map((key) => ({ data: key }))),
              );
            }
          } else {
            // 空配列の場合は明示的に空配列を送信（削除を意味する）
            formData.append(doc.key, JSON.stringify([]));
          }
        }
      }
    }

    // 削除された項目には空配列を送信（バックエンド側で削除されるように）
    existingOtherDocumentKeys.forEach((key) => {
      if (!currentOtherDocumentKeys.has(key)) {
        formData.append(key, JSON.stringify([]));
      }
    });

    try {
      setUpdatingFlag(true);
      const resultAction = await dispatch(
        updateProperty({ id, uploadFormData: formData }),
      );
      if ((updateProperty as any).fulfilled.match(resultAction)) {
        const refetch = await dispatch(fetchPropertyDetail(id));
        if (!fetchPropertyDetail.fulfilled.match(refetch)) {
          const payload = refetch.payload as ApiErrorPayload | undefined;
          setInitialLoadError(payload?.errorType ?? "unknown");
        }
        showToast({ type: "success", message: "物件情報を更新しました" });
      } else {
        showToast({
          type: "error",
          message:
            (resultAction as any)?.payload?.message || "更新に失敗しました",
        });
      }
    } catch (error: any) {
      console.error("物件更新エラー:", error);
      const message = "ファイルのアップロードまたは物件更新に失敗しました";
      showToast({ type: "error", message });
    } finally {
      setUpdatingFlag(false);
    }
  };

  const property = detailed?.data;
  const hasPropertyData = Boolean(property?.id);
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: hasPropertyData,
    dedupeKey: `property:${id ?? ""}`,
  });

  if (isCategoryValidationPending) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          width: "100%",
        }}
      >
        <CustomLoading loading={true} fullScreen={false} size="medium" />
      </Box>
    );
  }

  if (!hasValidTabQuery) {
    return <InitialLoadError errorType="notFound" />;
  }

  if (shouldShowInitialLoadError(initialLoadError, hasPropertyData)) {
    return (
      <InitialLoadError errorType={initialLoadError} onRetry={fetchDetail} />
    );
  }

  // 物件名: APIデータ優先、ロード中はナビゲーション state をフォールバック
  const propertyName = property?.name || statePropertyName || "";

  return (
    // data-entity / data-id: AI チャットへの画面コンテキスト同梱用 (pageContext.ts 参照)。
    // display:contents で既存レイアウトに影響を出さずに識別用ラッパーだけ DOM に残す。
    <div
      data-entity="property"
      data-id={id ?? ""}
      style={{ display: "contents" }}
    >
      <Box
        sx={{
          marginLeft: { lg: "-32px", xs: "-24px" },
        }}
      >
        <PropertyDetailHeader
          propertyName={propertyName}
          tabValue={tabValue}
          onTabChange={handleTabChange}
          propertyCategory={property?.property_category}
        />
      </Box>

      {/* タブコンテンツ */}
      <Box
        sx={{
          pt: {
            lg: `${TAB_CONTENT_PADDING_TOP_DESKTOP}px`,
            xs: `${TAB_CONTENT_PADDING_TOP_MOBILE}px`,
          },
        }}
      >
        {hasPropertyData ? (
          <>
            <TabPanel value={tabValue} index={0}>
              <PropertyOverview data={property} onSubmit={onUpdateProperty} />
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <RentRoll propertyId={property.id} propertyName={propertyName} />
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <Budget propertyId={property.id} propertyName={propertyName} />
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              <InspectionCleaning
                propertyId={property.id}
                propertyName={propertyName}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={4}>
              {/* 修繕タブ: 暫定で旧 RepairSubTab を直接配置 (⑫で本格対応) */}
              <RepairSubTab
                propertyId={property.id}
                propertyName={propertyName}
              />
            </TabPanel>
            <TabPanel value={tabValue} index={5}>
              <ExpenseManagement
                propertyId={property.id}
                propertyName={propertyName}
              />
            </TabPanel>
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
              width: "100%",
            }}
          >
            <CustomLoading loading={true} fullScreen={false} size="medium" />
          </Box>
        )}
      </Box>
    </div>
  );
};

export default PropertyDetailPage;
